import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaBars,
  FaBox,
  FaTags,
  FaShoppingCart,
  FaKey,
  FaTicketAlt,
  FaSignOutAlt,
  FaChartBar,
  FaComments,
  FaPlus,
  FaStar,
  FaRobot,
  FaDatabase,
  FaBug,
  FaEnvelope,
} from 'react-icons/fa';
import './AdminDashboard.css';
import SymbolSpecUploader from './SymbolSpecUploader';
import CopierEaUploader from './CopierEaUploader';
import CampaignsPanel from './CampaignsPanel';
import LedgerRebuildPanel from './LedgerRebuildPanel';
import VmDiagnosticsPanel from './VmDiagnosticsPanel';

function adminTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(window.atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return Boolean(payload?.exp && payload.exp * 1000 <= Date.now());
  } catch (error) {
    return true;
  }
}

function AdminDashboard() {
  const [products, setProducts] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [communityThreads, setCommunityThreads] = useState([]);
  const [communityComments, setCommunityComments] = useState([]);
  const [vixAiToolState, setVixAiToolState] = useState(null);
  const [vixAiToolSaving, setVixAiToolSaving] = useState(false);
  const [activeCommunityThread, setActiveCommunityThread] = useState(null);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [tab, setTab] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [salesSeries, setSalesSeries] = useState([]);
  const [trafficSeries, setTrafficSeries] = useState([]);
  const [licenseSeries, setLicenseSeries] = useState([]);
  const [subscriptionAnalytics, setSubscriptionAnalytics] = useState(null);
  const [subscriptionSeries, setSubscriptionSeries] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [searchTerms, setSearchTerms] = useState({});
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isRefreshingChat, setIsRefreshingChat] = useState(false);
  const [showClosedChats, setShowClosedChats] = useState(false);
  const [licenseEmail, setLicenseEmail] = useState('');
  const [licenseProductId, setLicenseProductId] = useState('');
  const [licenseFormMessage, setLicenseFormMessage] = useState('');
  const [isCreatingLicense, setIsCreatingLicense] = useState(false);
  const [selectedLicenseId, setSelectedLicenseId] = useState(null);
  const [selectedLicenseDevices, setSelectedLicenseDevices] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [adminConfirm, setAdminConfirm] = useState(null);
  const attachmentInputRef = useRef(null);
  const adminSidebarShellRef = useRef(null);
  const adminSidebarInnerRef = useRef(null);
  const [isAdminMobileNavPinned, setIsAdminMobileNavPinned] = useState(false);
  const [adminMobileNavStyle, setAdminMobileNavStyle] = useState({});
  const [adminMobileNavHeight, setAdminMobileNavHeight] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem('adminToken');
  const visibleConversations = conversations.filter((conversation) => conversation.status !== 'closed');
  const closedConversations = conversations.filter((conversation) => conversation.status === 'closed');

  const openAdminConfirm = useCallback((nextConfirm) => {
    setAdminConfirm({
      title: 'Confirm Action',
      message: '',
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      tone: '',
      ...nextConfirm,
    });
  }, []);

  const closeAdminConfirm = useCallback(() => {
    setAdminConfirm(null);
  }, []);

  const expireAdminSession = useCallback(() => {
    localStorage.removeItem('adminToken');
    window.dispatchEvent(new Event('auth-changed'));
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  const filterRows = (rows, term) => {
    if (!term) return rows;
    const lower = term.toLowerCase();
    return rows.filter((row) => JSON.stringify(row).toLowerCase().includes(lower));
  };

  const handleSearchChange = (key, value) => {
    setSearchTerms((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (adminTokenExpired(token)) {
      expireAdminSession();
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (adminTokenExpired(localStorage.getItem('adminToken'))) {
        expireAdminSession();
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [expireAdminSession, token]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [summaryRes, salesRes, trafficRes, licenseRes, subscriptionRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/analytics/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/analytics/sales`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/analytics/traffic`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/analytics/licenses`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/analytics/subscriptions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setAnalytics(summaryRes.data);
      setSalesSeries(salesRes.data || []);
      setTrafficSeries(trafficRes.data || []);
      setLicenseSeries(licenseRes.data || []);
      setSubscriptionAnalytics(subscriptionRes.data || null);
      setSubscriptionSeries(subscriptionRes.data?.series || []);
    } catch (err) {
      setError('Failed to load analytics.');
    }
  }, [token]);

  const fetchTableData = useCallback(async () => {
    try {
      let response;
      if (tab === 'products') {
        response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProducts(Array.isArray(response.data) ? response.data : (response.data.products || []));
      } else if (tab === 'promo-codes') {
        response = await axios.get(`${process.env.REACT_APP_API_URL}/api/promo-codes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPromoCodes(Array.isArray(response.data) ? response.data : []);
      } else if (tab === 'purchases') {
        response = await axios.get(`${process.env.REACT_APP_API_URL}/api/purchases`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPurchases(Array.isArray(response.data) ? response.data : []);
      } else if (tab === 'licenses') {
        const [licenseResponse, productResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/licenses`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/products`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setLicenses(Array.isArray(licenseResponse.data) ? licenseResponse.data : []);
        setProducts(Array.isArray(productResponse.data) ? productResponse.data : (productResponse.data.products || []));
      } else if (tab === 'tickets') {
        response = await axios.get(`${process.env.REACT_APP_API_URL}/api/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTickets(Array.isArray(response.data) ? response.data : []);
      } else if (tab === 'reviews') {
        response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/reviews`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReviews(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      setError('Failed to fetch data. Please log in again.');
    }
  }, [tab, token]);

  const fetchConversations = useCallback(async () => {
    try {
      setIsRefreshingChat(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setConversations(list);
      setActiveConversation((currentConversation) => {
        if (!currentConversation?.id) return currentConversation;
        return list.find((conversation) => conversation.id === currentConversation.id) || null;
      });
    } catch (err) {
      setError('Failed to load chat conversations.');
    } finally {
      setIsRefreshingChat(false);
    }
  }, [token]);

  const fetchCommunityThreads = useCallback(async () => {
    try {
      setCommunityLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/community/threads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setCommunityThreads(list);
      setActiveCommunityThread((currentThread) => {
        if (!currentThread?.id) return currentThread;
        return list.find((thread) => thread.id === currentThread.id) || null;
      });
    } catch (err) {
      setError('Failed to load community moderation data.');
    } finally {
      setCommunityLoading(false);
    }
  }, [token]);

  const fetchCommunityComments = useCallback(async (threadId) => {
    if (!threadId) return;
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/community/threads/${threadId}/comments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCommunityComments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('Failed to load community comments.');
    }
  }, [token]);

  const fetchVixAiToolState = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/vixai/tools`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVixAiToolState(response.data || null);
    } catch (err) {
      setError('Failed to load VixAI tool controls.');
    }
  }, [token]);

  const updateVixAiToolSetting = useCallback(async (key, value) => {
    const currentSettings = vixAiToolState?.settings || {};
    const nextSettings = {
      ...currentSettings,
      [key]: value,
    };
    setVixAiToolState((previous) => ({
      ...(previous || {}),
      settings: nextSettings,
    }));
    try {
      setVixAiToolSaving(true);
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/vixai/tools`,
        { settings: nextSettings },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setVixAiToolState((previous) => ({
        ...(previous || {}),
        settings: response.data?.settings || nextSettings,
      }));
    } catch (err) {
      setError('Failed to update VixAI tool controls.');
      await fetchVixAiToolState();
    } finally {
      setVixAiToolSaving(false);
    }
  }, [fetchVixAiToolState, token, vixAiToolState]);

  const fetchMessages = useCallback(
    async (conversationId) => {
      if (!conversationId) return;
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/chat/conversations/${conversationId}/messages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMessages(Array.isArray(response.data) ? response.data : []);
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/admin/chat/conversations/${conversationId}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        setError('Failed to load chat messages.');
      }
    },
    [token]
  );

  useEffect(() => {
    if (tab === 'overview') fetchAnalytics();
    if (tab === 'chat') fetchConversations();
    if (tab === 'community') fetchCommunityThreads();
    if (tab === 'vixai') fetchVixAiToolState();
    if (['products', 'promo-codes', 'purchases', 'licenses', 'tickets', 'reviews'].includes(tab)) {
      fetchTableData();
    }
  }, [tab, fetchAnalytics, fetchCommunityThreads, fetchConversations, fetchTableData, fetchVixAiToolState]);

  useEffect(() => {
    if (tab !== 'chat') return undefined;
    const intervalId = setInterval(() => {
      fetchConversations();
      if (activeConversation?.id) {
        fetchMessages(activeConversation.id);
      }
    }, 15000);
    return () => clearInterval(intervalId);
  }, [tab, activeConversation, fetchConversations, fetchMessages]);

  useEffect(() => {
    const updateAdminMobileSidebarPosition = () => {
      if (!adminSidebarShellRef.current || !adminSidebarInnerRef.current) return;

      if (window.innerWidth > 1024) {
        setIsAdminMobileNavPinned(false);
        setAdminMobileNavStyle({});
        setAdminMobileNavHeight(null);
        return;
      }

      const topOffset = 92;
      const shellRect = adminSidebarShellRef.current.getBoundingClientRect();
      const sidebarHeight = adminSidebarInnerRef.current.offsetHeight;
      const shouldPin = shellRect.top <= topOffset;

      setAdminMobileNavHeight(sidebarHeight);

      if (shouldPin) {
        setAdminMobileNavStyle({
          left: `${shellRect.left}px`,
          width: `${shellRect.width}px`,
          top: `${topOffset}px`,
        });
      } else {
        setAdminMobileNavStyle({});
      }

      setIsAdminMobileNavPinned(shouldPin);
    };

    updateAdminMobileSidebarPosition();
    window.addEventListener('scroll', updateAdminMobileSidebarPosition, { passive: true });
    window.addEventListener('resize', updateAdminMobileSidebarPosition);

    return () => {
      window.removeEventListener('scroll', updateAdminMobileSidebarPosition);
      window.removeEventListener('resize', updateAdminMobileSidebarPosition);
    };
  }, []);

  const performDelete = async (id, endpoint) => {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}${endpoint}/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchTableData();
      } catch (err) {
        setError('Failed to delete item');
      }
  };

  const handleDelete = async (id, endpoint) => {
    openAdminConfirm({
      title: 'Delete Item',
      message: 'Delete this item permanently?',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        closeAdminConfirm();
        await performDelete(id, endpoint);
      },
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    window.dispatchEvent(new Event('auth-changed'));
    navigate('/admin/login');
  };

  const handleConversationSelect = (conversation) => {
    setShowClosedChats(false);
    setActiveConversation(conversation);
    fetchMessages(conversation.id);
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!activeConversation || isSendingMessage) return;
    if (!messageText && attachments.length === 0) return;
    try {
      setIsSendingMessage(true);
      const formData = new FormData();
      formData.append('conversationId', activeConversation.id);
      formData.append('message', messageText);
      attachments.forEach((file) => formData.append('attachments', file));
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/chat/messages`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessageText('');
      setAttachments([]);
      fetchMessages(activeConversation.id);
      fetchConversations();
    } catch (err) {
      setError('Failed to send message.');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const toggleLicenseStatus = async (license) => {
    if (license.type !== 'device') return;
    const nextStatus = license.status === 'active' ? 'revoked' : 'active';
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/licenses/${license.id}/status`,
        { status: nextStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTableData();
    } catch (err) {
      setError('Failed to update license status.');
    }
  };

  const handleReviewStatus = async (id, status) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/reviews/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTableData();
    } catch (err) {
      setError('Failed to update review status.');
    }
  };

  const handleCommunityThreadStatus = async (threadId, status) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/community/threads/${threadId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCommunityThreads();
      setActiveCommunityThread((currentThread) => (
        currentThread?.id === threadId ? { ...currentThread, status } : currentThread
      ));
    } catch (err) {
      setError('Failed to update community thread status.');
    }
  };

  const handleCommunityThreadSelect = async (thread) => {
    setActiveCommunityThread(thread);
    await fetchCommunityComments(thread.id);
  };

  const handleDeleteCommunityComment = async (commentId) => {
    openAdminConfirm({
      title: 'Delete Comment',
      message: 'Delete this community comment?',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        closeAdminConfirm();
        try {
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/api/admin/community/comments/${commentId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (activeCommunityThread?.id) {
            await fetchCommunityComments(activeCommunityThread.id);
          }
          await fetchCommunityThreads();
        } catch (err) {
          setError('Failed to delete community comment.');
        }
      },
    });
  };

  const handleCreateLicense = async (event) => {
    event.preventDefault();
    if (!licenseEmail || !licenseProductId) {
      setLicenseFormMessage('Enter the customer email and select a product.');
      return;
    }
    try {
      setIsCreatingLicense(true);
      setLicenseFormMessage('');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/licenses`,
        { email: licenseEmail.trim(), productId: parseInt(licenseProductId, 10) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const licenseKey = response.data?.license?.licenseKey || response.data?.licenseKey;
      setLicenseFormMessage(
        response.data?.reused
          ? `Existing license found: ${licenseKey}`
          : `License created: ${licenseKey}`
      );
      setLicenseEmail('');
      setLicenseProductId('');
      fetchTableData();
    } catch (err) {
      setLicenseFormMessage(err.response?.data?.error || 'Failed to create license.');
    } finally {
      setIsCreatingLicense(false);
    }
  };

  const handleLoadDevices = async (licenseId) => {
    if (selectedLicenseId === licenseId) {
      setSelectedLicenseId(null);
      setSelectedLicenseDevices([]);
      return;
    }
    try {
      setIsLoadingDevices(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/licenses/${licenseId}/devices`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedLicenseId(licenseId);
      setSelectedLicenseDevices(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('Failed to load license devices.');
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const handleConversationStatus = async (conversationId, status) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/chat/conversations/${conversationId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (activeConversation?.id === conversationId) {
        setActiveConversation((prev) => prev ? { ...prev, status } : prev);
      }
      fetchConversations();
    } catch (err) {
      setError('Failed to update conversation status.');
    }
  };

  const renderMiniChart = (data, valueKey) => {
    const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
    return (
      <div className="mini-chart">
        {data.map((point) => (
          <div
            key={point.day}
            className="mini-bar"
            style={{ height: `${(point[valueKey] / max) * 100}%` }}
            title={`${point.day}: ${point[valueKey]}`}
          />
        ))}
      </div>
    );
  };

  const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

  return (
    <div className={`admin-dashboard ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <aside
        ref={adminSidebarShellRef}
        className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${isAdminMobileNavPinned ? 'mobile-shell-pinned' : ''}`}
        style={isAdminMobileNavPinned && adminMobileNavHeight ? { height: `${adminMobileNavHeight}px` } : undefined}
      >
        <div
          ref={adminSidebarInnerRef}
          className={`admin-sidebar-inner ${isAdminMobileNavPinned ? 'pinned-mobile' : ''}`}
          style={isAdminMobileNavPinned ? adminMobileNavStyle : undefined}
        >
          <div className="admin-brand">
            <div>
              <p className="admin-title">Vix Admin</p>
              <p className="admin-subtitle">Operations</p>
            </div>
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setSidebarCollapsed((current) => !current)}
              aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
            >
              <FaBars />
            </button>
          </div>
          <nav className="admin-nav">
            <button className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>
              <FaChartBar />
              <span>Overview</span>
            </button>
            <button className={tab === 'chat' ? 'active' : ''} onClick={() => setTab('chat')}>
              <FaComments />
              <span>Chat</span>
            </button>
            <button className={tab === 'products' ? 'active' : ''} onClick={() => setTab('products')}>
              <FaBox />
              <span>Products</span>
            </button>
            <button className={tab === 'promo-codes' ? 'active' : ''} onClick={() => setTab('promo-codes')}>
              <FaTags />
              <span>Promo Codes</span>
            </button>
            <button className={tab === 'purchases' ? 'active' : ''} onClick={() => setTab('purchases')}>
              <FaShoppingCart />
              <span>Purchases</span>
            </button>
            <button className={tab === 'licenses' ? 'active' : ''} onClick={() => setTab('licenses')}>
              <FaKey />
              <span>Licenses</span>
            </button>
            <button className={tab === 'tickets' ? 'active' : ''} onClick={() => setTab('tickets')}>
              <FaTicketAlt />
              <span>Tickets</span>
            </button>
            <button className={tab === 'reviews' ? 'active' : ''} onClick={() => setTab('reviews')}>
              <FaStar />
              <span>Reviews</span>
            </button>
            <button className={tab === 'community' ? 'active' : ''} onClick={() => setTab('community')}>
              <FaComments />
              <span>Community</span>
            </button>
            <button className={tab === 'vixai' ? 'active' : ''} onClick={() => setTab('vixai')}>
              <FaRobot />
              <span>VixAI</span>
            </button>
            <button className={tab === 'campaigns' ? 'active' : ''} onClick={() => setTab('campaigns')}>
              <FaEnvelope />
              <span>Email</span>
            </button>
            <button className={tab === 'maintenance' ? 'active' : ''} onClick={() => setTab('maintenance')}>
              <FaDatabase />
              <span>Maintenance</span>
            </button>
            <button className={tab === 'copier-ea' ? 'active' : ''} onClick={() => setTab('copier-ea')}>

              <FaDatabase />

              <span>Copier EA</span>

            </button>
            <button className={tab === 'symbol-specs' ? 'active' : ''} onClick={() => setTab('symbol-specs')}>
              <FaDatabase />
              <span>Symbol Specs</span>
            </button>
            <button className={tab === 'vm-errors' ? 'active' : ''} onClick={() => setTab('vm-errors')}>
              <FaBug />
              <span>VM Errors</span>
            </button>
            <button className="logout" onClick={handleLogout}>
              <FaSignOutAlt />
              <span>Logout</span>
            </button>
          </nav>
        </div>
      </aside>

      <main className="admin-content">
        {error && <div className="section-card">{error}</div>}

        {tab === 'symbol-specs' && <SymbolSpecUploader />}

        {tab === 'vm-errors' && <VmDiagnosticsPanel />}

        {tab === 'campaigns' && <CampaignsPanel />}

        {tab === 'maintenance' && <LedgerRebuildPanel />}

        {tab === 'copier-ea' && <CopierEaUploader />}

        {tab === 'overview' && (
          <div className="overview">
            <div className="overview-header">
              <div>
                <span className="tag">Analytics</span>
                <h1 className="page-title">Store performance</h1>
                <p className="page-lead">Product sales, subscriptions, licenses, users, and traffic in one place.</p>
              </div>
            </div>
            <div className="admin-overview-section">
              <div>
                <span className="tag">Product Sales</span>
                <h2>One-time purchases</h2>
              </div>
            </div>
            <div className="overview-grid">
              <div className="stat-card">
                <p className="stat-label">Revenue</p>
                <h3>{formatMoney(analytics?.totalRevenue)}</h3>
                <p className="muted-text">Product purchase revenue.</p>
                {renderMiniChart(salesSeries, 'revenue')}
              </div>
              <div className="stat-card">
                <p className="stat-label">Sales</p>
                <h3>{analytics?.totalSales || 0}</h3>
                <p className="muted-text">Total orders.</p>
                {renderMiniChart(salesSeries, 'orders')}
              </div>
            </div>

            <div className="admin-overview-section">
              <div>
                <span className="tag">VixPremium</span>
                <h2>Subscriptions</h2>
              </div>
              <p className="muted-text">
                Revenue is separate from product purchases.
                {subscriptionAnalytics?.revenueSource === 'latest_subscription_snapshot' ? ' Using latest PayPal subscription snapshot until payment webhooks are available.' : ''}
              </p>
            </div>
            <div className="overview-grid">
              <div className="stat-card subscription">
                <p className="stat-label">Subscription revenue</p>
                <h3>{formatMoney(subscriptionAnalytics?.subscriptionRevenue)}</h3>
                <p className="muted-text">Captured subscription payments.</p>
                {renderMiniChart(subscriptionSeries, 'revenue')}
              </div>
              <div className="stat-card subscription">
                <p className="stat-label">Active subs</p>
                <h3>{subscriptionAnalytics?.activeSubscriptions || 0}</h3>
                <p className="muted-text">{subscriptionAnalytics?.totalSubscriptions || 0} total premium subscriptions.</p>
              </div>
              <div className="stat-card subscription">
                <p className="stat-label">Estimated MRR</p>
                <h3>{formatMoney(subscriptionAnalytics?.activeMonthlyValue)}</h3>
                <p className="muted-text">Based on active subscriptions with known payment amount.</p>
              </div>
              <div className="stat-card subscription">
                <p className="stat-label">Pending / cancelled</p>
                <h3>{subscriptionAnalytics?.pendingSubscriptions || 0} / {subscriptionAnalytics?.cancelledSubscriptions || 0}</h3>
                <p className="muted-text">{subscriptionAnalytics?.recentSubscriptions || 0} new in the last {subscriptionAnalytics?.days || 30} days.</p>
              </div>
            </div>

            <div className="admin-overview-section">
              <div>
                <span className="tag">Operations</span>
                <h2>Licenses and traffic</h2>
              </div>
            </div>
            <div className="overview-grid">
              <div className="stat-card">
                <p className="stat-label">Active licenses</p>
                <h3>{analytics?.activeLicenses || 0}</h3>
                <p className="muted-text">Device-bound licenses.</p>
                {renderMiniChart(licenseSeries, 'issued')}
              </div>
              <div className="stat-card">
                <p className="stat-label">Page views</p>
                <h3>{analytics?.traffic || 0}</h3>
                <p className="muted-text">{analytics?.trafficVisitors || 0} unique visitors in the last {analytics?.days || 30} days.</p>
                {renderMiniChart(trafficSeries, 'views')}
              </div>
              <div className="stat-card">
                <p className="stat-label">Active users</p>
                <h3>{analytics?.userCount || 0}</h3>
                <p className="muted-text">Non-deleted user accounts.</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Legacy licenses</p>
                <h3>{analytics?.legacyLicenses || 0}</h3>
                <p className="muted-text">Old license records still retained.</p>
              </div>
            </div>
          </div>
        )}

        {tab === 'chat' && (
          <div className="chat-layout">
            <div className="chat-list section-card">
              <h2>Conversations</h2>
              <div className="chat-search">
                <input
                  type="text"
                  placeholder="Search conversations"
                  value={searchTerms.chat || ''}
                  onChange={(e) => handleSearchChange('chat', e.target.value)}
                />
              </div>
              <button type="button" className="ghost-button" onClick={fetchConversations}>
                {isRefreshingChat ? 'Refreshing...' : 'Refresh'}
              </button>
              <div className="chat-threads">
                {filterRows(visibleConversations, searchTerms.chat || '').map((convo) => (
                  <button
                    key={convo.id}
                    className={`chat-thread ${activeConversation?.id === convo.id ? 'active' : ''}`}
                    onClick={() => handleConversationSelect(convo)}
                  >
                    <div>
                      <p className="thread-title">{convo.subject || 'Support'}</p>
                      <p className="thread-meta">{convo.userEmail}</p>
                    </div>
                    {convo.unreadCount > 0 && <span className="badge">{convo.unreadCount}</span>}
                  </button>
                ))}
                {filterRows(closedConversations, searchTerms.chat || '').length > 0 && (
                  <>
                    <button
                      type="button"
                      className="ghost-button chat-toggle-button"
                      onClick={() => setShowClosedChats((current) => !current)}
                    >
                      {showClosedChats
                        ? 'Hide Other Chats'
                        : `Show Other Chats (${filterRows(closedConversations, searchTerms.chat || '').length})`}
                    </button>
                    {showClosedChats && filterRows(closedConversations, searchTerms.chat || '').map((convo) => (
                      <button
                        key={convo.id}
                        className={`chat-thread ${activeConversation?.id === convo.id ? 'active' : ''}`}
                        onClick={() => handleConversationSelect(convo)}
                      >
                        <div>
                          <p className="thread-title">{convo.subject || 'Support'}</p>
                          <p className="thread-meta">{convo.userEmail}</p>
                        </div>
                        {convo.unreadCount > 0 && <span className="badge">{convo.unreadCount}</span>}
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
            <div className="chat-panel section-card">
              {activeConversation ? (
                <>
                  <div className="chat-header">
                    <div>
                      <h3>{activeConversation.subject || 'Support'}</h3>
                      <p className="muted-text">{activeConversation.userEmail}</p>
                    </div>
                    <div className="chat-header-actions">
                      <span className={`status-pill ${activeConversation.status}`}>{activeConversation.status}</span>
                      {activeConversation.status === 'open' && (
                        <button type="button" className="ghost-button" onClick={() => handleConversationStatus(activeConversation.id, 'closed')}>
                          Close Issue
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="chat-messages">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`chat-row ${msg.senderRole === 'admin' ? 'outgoing' : 'incoming'}`}
                      >
                        <div className={`chat-bubble ${msg.senderRole}`}>
                          {msg.message && <p>{msg.message}</p>}
                          {msg.attachments?.length > 0 && (
                            <div className="chat-attachments">
                              {msg.attachments.map((att) => (
                                <a
                                  key={att.id}
                                  href={`${process.env.REACT_APP_API_URL}/${att.filePath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {att.originalName || 'Attachment'}
                                </a>
                              ))}
                            </div>
                          )}
                          <div className="message-footer">
                            <span className="message-time">
                              {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {msg.senderRole === 'admin' && (
                              <span className="read-receipt">
                                {msg.readAtUser ? String.fromCharCode(10003, 10003) : String.fromCharCode(10003)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <form className="chat-input" onSubmit={handleSendMessage}>
                    <input
                      type="text"
                      placeholder="Write a reply..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      disabled={isSendingMessage || activeConversation.status === 'closed'}
                    />
                    <div className="chat-actions">
                      <label
                        className={`chat-upload-button ${isSendingMessage || activeConversation.status === 'closed' ? 'disabled' : ''}`}
                        aria-label="Add attachments"
                      >
                        <FaPlus />
                        <input
                          ref={attachmentInputRef}
                          type="file"
                          multiple
                          disabled={isSendingMessage || activeConversation.status === 'closed'}
                          onChange={(e) => setAttachments(Array.from(e.target.files || []))}
                        />
                      </label>
                      {attachments.length > 0 && (
                        <span className="chat-attachment-count">{attachments.length} file{attachments.length > 1 ? 's' : ''}</span>
                      )}
                      <button type="submit" disabled={isSendingMessage || activeConversation.status === 'closed'}>
                        {isSendingMessage ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="chat-empty">
                  <p>Select a conversation to start replying.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'products' && (
          <div className="section-card">
            <div className="table-header">
              <div>
                <h2>Products</h2>
                <p className="muted-text">Manage product catalog and assets.</p>
              </div>
              <div className="table-actions">
                <input
                  type="text"
                  placeholder="Search products"
                  value={searchTerms.products || ''}
                  onChange={(e) => handleSearchChange('products', e.target.value)}
                />
                <Link to="/admin/add-product" className="primary-cta compact">Add Product</Link>
              </div>
            </div>
            <div className="table-shell">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Price</th>
                    <th>Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filterRows(products, searchTerms.products || '').length > 0 ? (
                    filterRows(products, searchTerms.products || '').map((p) => (
                      <tr key={p.id}>
                        <td>{p.id}</td>
                        <td>{p.name}</td>
                        <td>${p.price}</td>
                        <td>{p.type}</td>
                        <td className="row-actions">
                          <Link to={`/admin/edit-product/${p.id}`}>Edit</Link>
                          <button onClick={() => handleDelete(p.id, '/api/products')}>Delete</button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5">No products found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'promo-codes' && (
          <div className="section-card">
            <div className="table-header">
              <div>
                <h2>Promo codes</h2>
                <p className="muted-text">Create and manage discounts.</p>
              </div>
              <div className="table-actions">
                <input
                  type="text"
                  placeholder="Search promo codes"
                  value={searchTerms.promos || ''}
                  onChange={(e) => handleSearchChange('promos', e.target.value)}
                />
                <Link to="/admin/add-promo" className="primary-cta compact">Add Promo Code</Link>
              </div>
            </div>
            <div className="table-shell">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Code</th>
                    <th>Discount %</th>
                    <th>Expiration</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filterRows(promoCodes, searchTerms.promos || '').length > 0 ? (
                    filterRows(promoCodes, searchTerms.promos || '').map((pc) => (
                      <tr key={pc.id}>
                        <td>{pc.id}</td>
                        <td>{pc.code}</td>
                        <td>{pc.discount_percentage}</td>
                        <td>{pc.expiration_date}</td>
                        <td>{pc.effective_status || (pc.is_active ? 'active' : 'inactive')}</td>
                        <td className="row-actions">
                          <Link to={`/admin/edit-promo/${pc.id}`}>Edit</Link>
                          <button onClick={() => handleDelete(pc.id, '/api/promo-codes')}>Delete</button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="6">No promo codes found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'purchases' && (
          <div className="section-card">
            <div className="table-header">
              <div>
                <h2>Purchases</h2>
                <p className="muted-text">Track orders and revenue.</p>
              </div>
              <div className="table-actions">
                <input
                  type="text"
                  placeholder="Search purchases"
                  value={searchTerms.purchases || ''}
                  onChange={(e) => handleSearchChange('purchases', e.target.value)}
                />
              </div>
            </div>
            <div className="table-shell">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Order ID</th>
                    <th>Product ID</th>
                    <th>Email</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filterRows(purchases, searchTerms.purchases || '').length > 0 ? (
                    filterRows(purchases, searchTerms.purchases || '').map((p) => (
                      <tr key={p.id}>
                        <td>{p.id}</td>
                        <td>{p.orderId}</td>
                        <td>{p.productId}</td>
                        <td>{p.email}</td>
                        <td>${p.amountPaid || '0.00'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5">No purchases found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'licenses' && (
          <div className="section-card">
            <div className="table-header">
              <div>
                <h2>Licenses</h2>
                <p className="muted-text">Manage active and legacy licenses.</p>
              </div>
              <div className="table-actions">
                <input
                  type="text"
                  placeholder="Search licenses"
                  value={searchTerms.licenses || ''}
                  onChange={(e) => handleSearchChange('licenses', e.target.value)}
                />
              </div>
            </div>
            <form className="license-creator" onSubmit={handleCreateLicense}>
              <div className="license-creator-grid">
                <input
                  type="email"
                  placeholder="Customer email"
                  value={licenseEmail}
                  onChange={(e) => setLicenseEmail(e.target.value)}
                />
                <select value={licenseProductId} onChange={(e) => setLicenseProductId(e.target.value)}>
                  <option value="">Select product</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
                <button type="submit" disabled={isCreatingLicense}>
                  {isCreatingLicense ? 'Creating...' : 'Create License'}
                </button>
              </div>
              {licenseFormMessage && <p className="muted-text">{licenseFormMessage}</p>}
            </form>
            <div className="table-shell">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Product</th>
                    <th>License Key</th>
                    <th>Email</th>
                    <th>Devices</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filterRows(licenses, searchTerms.licenses || '').length > 0 ? (
                    filterRows(licenses, searchTerms.licenses || '').map((l) => (
                      <tr key={l.id}>
                        <td>{l.id}</td>
                        <td>{l.productName || l.productId || '-'}</td>
                        <td className="license-key">{l.licenseKey}</td>
                        <td>{l.email || ''}</td>
                        <td>{l.type === 'device' ? `${l.deviceCount || 0}/${l.deviceLimit || 2}` : '-'}</td>
                        <td>{l.status || 'legacy'}</td>
                        <td className="row-actions">
                          {l.type === 'device' ? (
                            <>
                              <button onClick={() => toggleLicenseStatus(l)}>
                                {l.status === 'active' ? 'Revoke' : 'Activate'}
                              </button>
                              <button type="button" className="ghost-button" onClick={() => handleLoadDevices(l.id)}>
                                {selectedLicenseId === l.id ? 'Hide Devices' : 'View Devices'}
                              </button>
                            </>
                          ) : (
                            <span className="muted-text">Legacy</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="7">No licenses found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {selectedLicenseId && (
              <div className="license-devices">
                <div className="table-header">
                  <div>
                    <h3>Bound devices</h3>
                    <p className="muted-text">Devices currently attached to license #{selectedLicenseId}.</p>
                  </div>
                  {isLoadingDevices && <p className="muted-text">Loading...</p>}
                </div>
                <div className="table-shell">
                  <table>
                    <thead>
                      <tr>
                        <th>Device ID</th>
                        <th>First Seen</th>
                        <th>Last Seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedLicenseDevices.length > 0 ? (
                        selectedLicenseDevices.map((device) => (
                          <tr key={device.id}>
                            <td className="license-key">{device.deviceId}</td>
                            <td>{device.createdAt}</td>
                            <td>{device.lastSeenAt}</td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan="3">No devices bound yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'tickets' && (
          <div className="section-card">
            <div className="table-header">
              <div>
                <h2>Tickets</h2>
                <p className="muted-text">Support requests from visitors.</p>
              </div>
              <div className="table-actions">
                <input
                  type="text"
                  placeholder="Search tickets"
                  value={searchTerms.tickets || ''}
                  onChange={(e) => handleSearchChange('tickets', e.target.value)}
                />
              </div>
            </div>
            <div className="table-shell">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Message</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {filterRows(tickets, searchTerms.tickets || '').length > 0 ? (
                    filterRows(tickets, searchTerms.tickets || '').map((t) => (
                      <tr key={t.id}>
                        <td>{t.id}</td>
                        <td>{t.name}</td>
                        <td>{t.email}</td>
                        <td className="ticket-message">{t.message}</td>
                        <td>{t.created_at}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5">No tickets found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'vixai' && (
          <div className="section-card">
            <div className="table-header">
              <div>
                <h2>VixAI Controls</h2>
                <p className="muted-text">Manage agent tools, web search, code gates, and optimization limits.</p>
              </div>
              <div className="table-actions">
                <button type="button" className="ghost-button" onClick={fetchVixAiToolState} disabled={vixAiToolSaving}>
                  {vixAiToolSaving ? 'Saving...' : 'Refresh'}
                </button>
              </div>
            </div>
            <div className="admin-settings-grid">
              {[
                ['toolsEnabled', 'Enable VixAI tools', 'Master switch for all agent actions.'],
                ['runBacktestEnabled', 'Allow backtest actions', 'Lets VixAI propose server backtest runs.'],
                ['exportMq5Enabled', 'Allow MQ5 export actions', 'Lets VixAI propose EA source exports.'],
                ['saveStrategyEnabled', 'Allow save actions', 'Lets VixAI propose saving strategies to user vaults.'],
                ['webSearchEnabled', 'Allow web search', 'Requires a configured Brave, Bing, or SerpAPI key.'],
                ['webSearchAutoRun', 'Auto-run web search', 'If off, users approve each search first.'],
                ['codeToolsEnabled', 'Allow code tools', 'Keep off until compiler/validator infrastructure is connected.'],
                ['optimizationEnabled', 'Allow optimization variants', 'Lets VixAI propose multi-backtest variant runs.'],
              ].map(([key, label, help]) => (
                <label key={key} className="admin-setting-row">
                  <input
                    type="checkbox"
                    checked={!!vixAiToolState?.settings?.[key]}
                    onChange={(event) => updateVixAiToolSetting(key, event.target.checked)}
                  />
                  <span>
                    <strong>{label}</strong>
                    <small>{help}</small>
                  </span>
                </label>
              ))}
              <label className="admin-setting-row">
                <span>
                  <strong>Web searches per user/day</strong>
                  <small>0 disables the daily cap.</small>
                </span>
                <input
                  type="number"
                  min="0"
                  max="500"
                  value={vixAiToolState?.settings?.webSearchRateLimitPerDay ?? 20}
                  onChange={(event) => updateVixAiToolSetting('webSearchRateLimitPerDay', event.target.value)}
                />
              </label>
              <label className="admin-setting-row">
                <span>
                  <strong>Max optimization variants</strong>
                  <small>Caps how many backtests VixAI can request in one optimization action.</small>
                </span>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={vixAiToolState?.settings?.maxOptimizationVariants ?? 6}
                  onChange={(event) => updateVixAiToolSetting('maxOptimizationVariants', event.target.value)}
                />
              </label>
            </div>
            <div className="admin-status-strip">
              <span>Web provider: {vixAiToolState?.webSearchProvider?.provider || 'not set'}</span>
              <span>{vixAiToolState?.webSearchProvider?.configured ? 'Configured' : 'Not configured'}</span>
            </div>
            <div className="table-shell">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>User</th>
                    <th>Tool</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {(vixAiToolState?.recentEvents || []).length ? (
                    vixAiToolState.recentEvents.map((event) => (
                      <tr key={event.id}>
                        <td>{new Date(event.createdAt).toLocaleString()}</td>
                        <td>{event.userEmail || event.userId || '-'}</td>
                        <td>{event.tool}</td>
                        <td>{event.status}</td>
                        <td>{event.details ? JSON.stringify(event.details).slice(0, 180) : '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5">No VixAI tool events yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'community' && (
          <div className="admin-community-grid">
            <div className="section-card">
              <div className="table-header">
                <div>
                  <h2>Community Moderation</h2>
                  <p className="muted-text">Control what appears in the VixPremium community.</p>
                </div>
                <div className="table-actions">
                  <input
                    type="text"
                    placeholder="Search community"
                    value={searchTerms.community || ''}
                    onChange={(e) => handleSearchChange('community', e.target.value)}
                  />
                  <button type="button" className="ghost-button" onClick={fetchCommunityThreads}>
                    {communityLoading ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
              </div>
              <div className="table-shell">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Thread</th>
                      <th>Author</th>
                      <th>Status</th>
                      <th>Activity</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterRows(communityThreads, searchTerms.community || '').length > 0 ? (
                      filterRows(communityThreads, searchTerms.community || '').map((thread) => (
                        <tr key={thread.id} className={activeCommunityThread?.id === thread.id ? 'active-row' : ''}>
                          <td>{thread.id}</td>
                          <td className="community-thread-cell">
                            <button type="button" className="link-button" onClick={() => handleCommunityThreadSelect(thread)}>
                              {thread.title}
                            </button>
                            <p>{thread.strategyName || thread.category} {thread.symbol ? `· ${thread.symbol} ${thread.timeframe || ''}` : ''}</p>
                          </td>
                          <td>{thread.authorEmail || thread.authorName || 'Member'}</td>
                          <td><span className={`status-pill ${thread.status}`}>{thread.status}</span></td>
                          <td>{thread.commentCount || 0} comments · {thread.attachmentCount || 0} files</td>
                          <td className="row-actions">
                            <button onClick={() => handleCommunityThreadSelect(thread)}>View</button>
                            <button onClick={() => handleCommunityThreadStatus(thread.id, 'active')} disabled={thread.status === 'active'}>Restore</button>
                            <button onClick={() => handleCommunityThreadStatus(thread.id, 'hidden')} disabled={thread.status === 'hidden'}>Hide</button>
                            <button onClick={() => handleCommunityThreadStatus(thread.id, 'rejected')} disabled={thread.status === 'rejected'}>Reject</button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="6">No community threads found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="section-card admin-community-comments">
              {activeCommunityThread ? (
                <>
                  <div className="table-header">
                    <div>
                      <h2>Thread Comments</h2>
                      <p className="muted-text">{activeCommunityThread.title}</p>
                    </div>
                    <button type="button" className="ghost-button" onClick={() => fetchCommunityComments(activeCommunityThread.id)}>Refresh</button>
                  </div>
                  <div className="community-comment-list">
                    {communityComments.length ? communityComments.map((comment) => (
                      <div key={comment.id} className="community-comment-card">
                        <div>
                          <strong>{comment.authorEmail || comment.authorName || 'Member'}</strong>
                          <span>{comment.createdAt}</span>
                        </div>
                        <p>{comment.body}</p>
                        <button type="button" className="ghost-button danger" onClick={() => handleDeleteCommunityComment(comment.id)}>
                          Delete Comment
                        </button>
                      </div>
                    )) : (
                      <p className="muted-text">No comments in this thread.</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="chat-empty">
                  <p>Select a community thread to inspect comments.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'reviews' && (
          <div className="section-card">
            <div className="table-header">
              <div>
                <h2>Reviews</h2>
                <p className="muted-text">Approve or reject customer reviews.</p>
              </div>
              <div className="table-actions">
                <input
                  type="text"
                  placeholder="Search reviews"
                  value={searchTerms.reviews || ''}
                  onChange={(e) => handleSearchChange('reviews', e.target.value)}
                />
              </div>
            </div>
            <div className="table-shell">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Product</th>
                      <th>User</th>
                      <th>Rating</th>
                      <th>Review</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                <tbody>
                  {filterRows(reviews, searchTerms.reviews || '').length > 0 ? (
                    filterRows(reviews, searchTerms.reviews || '').map((r) => (
                      <tr key={r.id}>
                        <td>{r.id}</td>
                        <td>{r.productName || r.productId}</td>
                        <td>{r.userEmail || r.userName || 'User'}</td>
                        <td>{r.rating}</td>
                        <td className="review-cell">
                          <p className="review-cell-title">{r.title || 'Untitled review'}</p>
                          <p className="review-cell-body">{r.body || 'No review body provided.'}</p>
                        </td>
                        <td>{r.status}</td>
                        <td className="row-actions">
                          <button
                            onClick={() => handleReviewStatus(r.id, 'approved')}
                            disabled={r.status === 'approved'}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReviewStatus(r.id, 'rejected')}
                            disabled={r.status === 'rejected'}
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="7">No reviews found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
      {adminConfirm && (
        <div className="admin-modal-overlay" role="presentation" onClick={closeAdminConfirm}>
          <div className={`admin-confirm-modal ${adminConfirm.tone || ''}`} role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="admin-confirm-header">
              <h3>{adminConfirm.title}</h3>
              <button type="button" onClick={closeAdminConfirm}>x</button>
            </div>
            <p>{adminConfirm.message}</p>
            <div className="admin-confirm-actions">
              <button type="button" className="ghost-button" onClick={closeAdminConfirm}>{adminConfirm.cancelLabel}</button>
              <button
                type="button"
                className={`primary-cta compact ${adminConfirm.tone === 'danger' ? 'danger' : ''}`}
                onClick={() => {
                  if (typeof adminConfirm.onConfirm === 'function') {
                    adminConfirm.onConfirm();
                  } else {
                    closeAdminConfirm();
                  }
                }}
              >
                {adminConfirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;


